import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Key, ArrowLeft } from '@phosphor-icons/react';
import { errorStatus, extractErrorMessage } from '../../lib/error';
import { useVerifyLead } from '../../hooks/useVerifyLead';
import { useResendLeadOtp } from '../../hooks/useResendLeadOtp';

interface OtpVerifyPanelProps {
  email: string;
  ownerName: string;
  onVerified: (ownerName: string) => void;
  onEditDetails: () => void;
}

/** Client resend cooldown — aligned to the backend resend @Throttle / OTP REQUEST_LIMIT (FE-Q1). */
const COOLDOWN_SECONDS = 30;

const UNIFORM_VERIFY_ERROR = 'That code is invalid or has expired. Check it and try again.';

/**
 * STATE B — OTP entry. Mirrors GuestLoginPage's code step (numeric input, resend,
 * one role="alert" banner) and adds the lead-flow affordances: edit-details,
 * resend confirm, and a client cooldown countdown.
 */
export function OtpVerifyPanel({ email, ownerName, onVerified, onEditDetails }: OtpVerifyPanelProps) {
  const [code, setCode] = useState('');
  const [resendConfirm, setResendConfirm] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef<HTMLInputElement>(null);

  const verify = useVerifyLead();
  const resend = useResendLeadOtp();

  // Focus the code input on mount (A→B focus handoff).
  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // Single cooldown interval, cleared on unmount and when it hits 0.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return;
    verify.mutate(
      { email, code },
      {
        onSuccess: () => onVerified(ownerName),
        onError: () => {
          // Keep the code, return focus so the user can correct it in place.
          codeRef.current?.focus();
        },
      },
    );
  }

  function handleResend() {
    setResendConfirm(null);
    resend.mutate(
      { email },
      {
        onSuccess: (data) => {
          setCode('');
          setResendConfirm(`We sent a new code to ${email}.`);
          setCooldown(COOLDOWN_SECONDS);
          codeRef.current?.focus();
          if (data.devCode) setCode(data.devCode); // non-prod convenience only
        },
        onError: (err) => {
          // Only lock the button on a genuine rate-limit (429). A transient
          // 500 / network error means no code was sent, so the guest should be
          // able to retry immediately rather than wait out a phantom cooldown.
          if (errorStatus(err) === 429) setCooldown(COOLDOWN_SECONDS);
        },
      },
    );
  }

  // One banner per surface: verify error OR resend error, newest replaces.
  const verifyError = verify.isError
    ? errorStatus(verify.error) === 429
      ? 'Too many attempts — please wait a moment.'
      : UNIFORM_VERIFY_ERROR
    : null;
  const resendError = resend.isError
    ? errorStatus(resend.error) === 429
      ? 'Please wait a moment before requesting another code.'
      : extractErrorMessage(resend.error)
    : null;
  const banner = verifyError ?? resendError;

  const cooldownLabel = `Resend available in 0:${String(cooldown).padStart(2, '0')}`;

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100 sm:p-8">
      <Key className="mx-auto h-12 w-12 text-brand-blue" aria-hidden="true" />
      <h2 className="mt-4 text-center text-xl font-bold text-brand-navy">Enter your code</h2>
      <p className="mt-1 text-center text-sm text-gray-500">
        We sent a 6-digit code to <strong>{email}</strong>.
      </p>

      <form onSubmit={submitCode} noValidate className="mt-6">
        {banner && (
          <p
            id="code-error"
            role="alert"
            className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
          >
            {banner}
          </p>
        )}

        <label htmlFor="code" className="text-sm font-semibold text-brand-navy">
          6-digit code
        </label>
        <input
          ref={codeRef}
          id="code"
          name="code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          aria-invalid={verify.isError}
          aria-describedby={verify.isError ? 'code-error' : undefined}
          className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 tracking-[0.5em] text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        />

        <button
          type="submit"
          disabled={verify.isPending || code.length !== 6}
          className="mt-4 w-full rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white hover:bg-brand-blue-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {verify.isPending ? 'Verifying…' : 'Verify & continue'}
        </button>
      </form>

      <p className="mt-3 text-center text-xs text-gray-500">The code expires in 10 minutes.</p>

      {resendConfirm && !banner && (
        <p role="status" className="mt-3 text-center text-sm font-medium text-brand-navy">
          {resendConfirm}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onEditDetails}
          className="inline-flex items-center gap-1 font-semibold text-brand-blue hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Edit details
        </button>
        <button
          type="button"
          disabled={resend.isPending || cooldown > 0}
          onClick={handleResend}
          className="font-semibold text-brand-blue hover:underline disabled:cursor-not-allowed disabled:opacity-60"
        >
          {resend.isPending ? 'Sending…' : cooldown > 0 ? cooldownLabel : 'Resend code'}
        </button>
      </div>
    </div>
  );
}
